import React, { useContext, useMemo, useState } from 'react';
import { FirebaseContext } from 'common';
import { ref as databaseRef, push } from 'firebase/database';
import { Box, Button, Chip, Container, Grid, Snackbar, TextField, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import { Link } from 'react-router-dom';
import { FONT_FAMILY } from '../common/sharedFunctions';
import eatsLogo from '../assets/img/eatslogo.png';

const GOLD = '#d6b62f';
const PANEL = 'rgba(255, 255, 255, 0.075)';

const initialForm = {
  restaurantName: '',
  ownerName: '',
  email: '',
  mobile: '',
  city: '',
  address: '',
  siret: '',
  cuisine: '',
  message: ''
};

const clampText = (value, max = 500) => String(value || '').trim().slice(0, max);

export default function RestaurantSignup() {
  const firebase = useContext(FirebaseContext);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, severity: 'success', text: '' });

  const canSubmit = useMemo(() => {
    return form.restaurantName.trim().length >= 2 &&
      form.ownerName.trim().length >= 2 &&
      /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()) &&
      form.mobile.trim().length >= 6 &&
      form.city.trim().length >= 2;
  }, [form]);

  const setField = (field, value) => setForm(current => ({ ...current, [field]: value }));

  const submitRequest = async (event) => {
    event.preventDefault();
    if (!canSubmit || !firebase?.database) {
      setFeedback({ open: true, severity: 'error', text: 'Merci de compléter les champs obligatoires.' });
      return;
    }
    setSubmitting(true);
    try {
      await push(databaseRef(firebase.database, 'restaurantAccessRequests'), {
        restaurantName: clampText(form.restaurantName, 80),
        ownerName: clampText(form.ownerName, 80),
        email: clampText(form.email, 120).toLowerCase(),
        mobile: clampText(form.mobile, 24),
        city: clampText(form.city, 60),
        address: clampText(form.address, 180),
        siret: clampText(form.siret, 32),
        cuisine: clampText(form.cuisine, 60),
        message: clampText(form.message, 500),
        requestedAccessType: 'restaurantOwner',
        oneAccessPerRestaurant: true,
        status: 'NEW',
        createdAt: Date.now()
      });
      setForm(initialForm);
      setFeedback({ open: true, severity: 'success', text: 'Demande envoyée. Notre équipe pourra créer l’accès restaurateur dédié.' });
    } catch (error) {
      setFeedback({ open: true, severity: 'error', text: 'La demande n’a pas pu être envoyée. Vérifiez les informations puis réessayez.' });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      color: '#fff',
      backgroundColor: 'rgba(255,255,255,0.065)',
      borderRadius: '14px',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.16)' },
      '&:hover fieldset': { borderColor: 'rgba(214,182,47,0.7)' },
      '&.Mui-focused fieldset': { borderColor: GOLD }
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
    '& .MuiInputLabel-root.Mui-focused': { color: GOLD }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#060604', color: '#fff', fontFamily: FONT_FAMILY, overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', inset: 0, opacity: 0.14, backgroundImage: 'linear-gradient(rgba(214,182,47,.22) 1px, transparent 1px), linear-gradient(90deg, rgba(214,182,47,.22) 1px, transparent 1px)', backgroundSize: '72px 72px' }} />
      <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 20% 20%, rgba(214,182,47,.18), transparent 34%), radial-gradient(circle at 82% 28%, rgba(6,193,103,.12), transparent 28%), linear-gradient(180deg, rgba(0,0,0,.2), #060604 78%)' }} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, py: { xs: 3, md: 5 }, px: { xs: 2, sm: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 4, md: 7 }, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '16px', border: `1px solid ${GOLD}`, display: 'grid', placeItems: 'center', backgroundColor: 'rgba(214,182,47,.08)', overflow: 'hidden' }}>
              <Box component="img" src={eatsLogo} alt="Allô Driver Eats" sx={{ width: 46, height: 46, borderRadius: '13px', objectFit: 'cover' }} />
            </Box>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 800, letterSpacing: 0 }}>Allô Driver Eats</Typography>
          </Box>
          <Button component={Link} to="/restaurant" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,.28)', color: '#fff', borderRadius: '12px', textTransform: 'none', px: 2.5 }}>
            Connexion restaurateur
          </Button>
        </Box>

        <Grid container spacing={{ xs: 3, md: 5 }} alignItems="stretch">
          <Grid item xs={12} md={6} lg={6.5} sx={{ display: 'flex' }}>
            <Box sx={{ alignSelf: 'center', maxWidth: 760 }}>
              <Chip label="Espace restaurateur" sx={{ mb: 3, backgroundColor: 'rgba(214,182,47,.13)', color: GOLD, border: `1px solid rgba(214,182,47,.45)`, fontWeight: 800 }} />
              <Typography component="h1" sx={{ fontFamily: FONT_FAMILY, fontWeight: 900, fontSize: { xs: 38, sm: 54, lg: 72 }, lineHeight: 0.98, letterSpacing: 0, mb: 3 }}>
                Pilotez vos commandes depuis une tablette.
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,.72)', fontSize: { xs: 17, md: 20 }, lineHeight: 1.65, maxWidth: 660, mb: 4 }}>
                Un accès restaurateur donne accès à un seul restaurant. Pour plusieurs établissements, chaque restaurant dispose de son propre accès et de ses propres commandes.
              </Typography>

              <Grid container spacing={2} sx={{ mb: 4 }}>
                {[
                  { icon: <ReceiptLongIcon />, title: 'Commandes', text: 'Suivi des nouvelles commandes et statuts de préparation.' },
                  { icon: <DashboardCustomizeIcon />, title: 'Gestion', text: 'Menu, documents, horaires et paiements au même endroit.' },
                  { icon: <TabletMacIcon />, title: 'Tablette', text: 'Interface adaptée au portrait comme au paysage.' }
                ].map(item => (
                  <Grid item xs={12} sm={4} key={item.title}>
                    <Box sx={{ height: '100%', p: 2, borderRadius: '18px', backgroundColor: PANEL, border: '1px solid rgba(255,255,255,.12)' }}>
                      <Box sx={{ color: GOLD, mb: 1 }}>{item.icon}</Box>
                      <Typography sx={{ fontWeight: 800, mb: .6 }}>{item.title}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,.62)', fontSize: 14, lineHeight: 1.45 }}>{item.text}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ display: { xs: 'none', md: 'block' }, p: 2.2, borderRadius: '24px', backgroundColor: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.13)', maxWidth: 680 }}>
                <Box sx={{ aspectRatio: '16 / 9', borderRadius: '18px', background: 'linear-gradient(135deg, rgba(214,182,47,.22), rgba(6,193,103,.1)), #111', border: '1px solid rgba(255,255,255,.12)', p: 2.4, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.6 }}>
                  <Box sx={{ borderRadius: '16px', backgroundColor: 'rgba(0,0,0,.42)', p: 2 }}>
                    <Typography sx={{ color: GOLD, fontWeight: 900, mb: 1 }}>Nouvelle commande</Typography>
                    <Typography sx={{ fontSize: 28, fontWeight: 900 }}>24,80 EUR</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,.62)', mt: 1 }}>2 plats - retrait cuisine</Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gap: 1.2 }}>
                    {['Accepter', 'Préparation', 'Prêt livreur'].map((label, index) => <Box key={label} sx={{ borderRadius: '14px', backgroundColor: index === 0 ? 'rgba(6,193,103,.22)' : 'rgba(255,255,255,.1)', border: `1px solid ${index === 0 ? 'rgba(6,193,103,.55)' : 'rgba(255,255,255,.12)'}`, p: 1.4, fontWeight: 800 }}>{label}</Box>)}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} lg={5.5}>
            <Box component="form" onSubmit={submitRequest} sx={{ p: { xs: 2, sm: 3, lg: 4 }, borderRadius: { xs: '24px', md: '32px' }, backgroundColor: 'rgba(15,15,12,.78)', border: '1px solid rgba(214,182,47,.32)', boxShadow: '0 24px 80px rgba(0,0,0,.35)', backdropFilter: 'blur(18px)' }}>
              <Typography sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 900, mb: 1 }}>Demander un accès</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,.65)', mb: 3, lineHeight: 1.55 }}>Nous créerons l’accès rattaché au restaurant après vérification des informations.</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField sx={fieldSx} fullWidth label="Restaurant" value={form.restaurantName} onChange={event => setField('restaurantName', event.target.value)} required /></Grid>
                <Grid item xs={12} sm={6}><TextField sx={fieldSx} fullWidth label="Responsable" value={form.ownerName} onChange={event => setField('ownerName', event.target.value)} required /></Grid>
                <Grid item xs={12} sm={6}><TextField sx={fieldSx} fullWidth type="email" label="Email" value={form.email} onChange={event => setField('email', event.target.value)} required /></Grid>
                <Grid item xs={12} sm={6}><TextField sx={fieldSx} fullWidth label="Telephone" value={form.mobile} onChange={event => setField('mobile', event.target.value)} required /></Grid>
                <Grid item xs={12} sm={6}><TextField sx={fieldSx} fullWidth label="Ville" value={form.city} onChange={event => setField('city', event.target.value)} required /></Grid>
                <Grid item xs={12} sm={6}><TextField sx={fieldSx} fullWidth label="Cuisine" value={form.cuisine} onChange={event => setField('cuisine', event.target.value)} /></Grid>
                <Grid item xs={12}><TextField sx={fieldSx} fullWidth label="Adresse" value={form.address} onChange={event => setField('address', event.target.value)} /></Grid>
                <Grid item xs={12}><TextField sx={fieldSx} fullWidth label="SIRET" value={form.siret} onChange={event => setField('siret', event.target.value)} /></Grid>
                <Grid item xs={12}><TextField sx={fieldSx} fullWidth multiline minRows={3} label="Message" value={form.message} onChange={event => setField('message', event.target.value)} /></Grid>
              </Grid>
              <Button type="submit" disabled={!canSubmit || submitting} fullWidth endIcon={<ArrowForwardIcon />} sx={{ mt: 3, height: 56, borderRadius: '16px', backgroundColor: GOLD, color: '#0a0904', fontWeight: 900, textTransform: 'none', fontSize: 16, '&:hover': { backgroundColor: '#f0d044' }, '&.Mui-disabled': { backgroundColor: 'rgba(214,182,47,.28)', color: 'rgba(255,255,255,.38)' } }}>
                {submitting ? 'Envoi...' : 'Envoyer la demande'}
              </Button>
              <Typography sx={{ mt: 2, color: 'rgba(255,255,255,.52)', fontSize: 13, lineHeight: 1.45 }}>
                L’espace connecté reste séparé : après création, le restaurateur se connecte depuis le bouton Connexion restaurateur.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Snackbar open={feedback.open} autoHideDuration={6000} onClose={() => setFeedback(current => ({ ...current, open: false }))}>
        <Alert severity={feedback.severity} variant="filled" onClose={() => setFeedback(current => ({ ...current, open: false }))}>{feedback.text}</Alert>
      </Snackbar>
    </Box>
  );
}